/**
 * C-GTCC-LOGIN-SEP1 — welche Oberflaeche ein Admin-Artefakt IST.
 *
 * # Der Fehler, den dieses Modul entfernt
 *
 * `apps/admin` wird dreimal aus derselben Quelle gebaut
 * (`infra/compose/app/edge/Dockerfile`): einmal mit `base=/admin/` nach
 * `/srv/admin`, einmal mit `VITE_BASE=/` nach `/srv/gtcc` (Cockpit) und
 * einmal mit `VITE_BASE=/` nach `/srv/gtcc1` (alte Konsole). Seit
 * D-GTCC-SHELL1 entschied allein `BASE_URL === "/"`, ob ein Bundle das
 * Cockpit ist — und das trifft auf BEIDE Root-Artefakte zu. gtcc1 zeigte
 * darum das neue Cockpit, obwohl es mit `GT_GTCC_SHELL_ENABLED=false`
 * gebaut wurde (gemessen in `docs/operations/EDGE_CSP_ENFORCE_PROOF.md`).
 *
 * # Warum eine eigene Bau-Eingabe und nicht die Flagge
 *
 * `GT_GTCC_SHELL_ENABLED` ist eine HOST-Variable (Vorgabe `false` in
 * `infra/compose/app/docker-compose.yml`). Haengt die Oberflaeche daran,
 * entscheidet eine Betriebseinstellung darueber, ob gtcc das Cockpit oder
 * die alte Konsole ausliefert. `GT_ADMIN_SURFACE` ist dagegen pro
 * `RUN`-Zeile im Dockerfile festgenagelt: es gehoert zum Artefakt, nicht
 * zum Host, und kann von aussen nicht umgelegt werden.
 *
 * Der Vorgabewert ist `gtcc1-console`: fehlt der Marker (fremder Build,
 * blanker Unit-Test, `vite dev`), ist das Bundle NICHT das Cockpit. Das
 * ist die engere der beiden Oberflaechen.
 */

/** Die Oberflaechen, die ein Admin-Artefakt sein kann. */
export const ADMIN_SURFACES = ["gtcc-cockpit", "gtcc1-console"] as const;

export type AdminSurface = (typeof ADMIN_SURFACES)[number];

/** Die Bau-Eingabe, die die Oberflaeche traegt. */
export const ADMIN_SURFACE_ENV_KEY = "GT_ADMIN_SURFACE";

/** Oberflaeche, wenn der Marker fehlt oder unbekannt ist. */
export const DEFAULT_ADMIN_SURFACE: AdminSurface = "gtcc1-console";

/**
 * Name des `<meta>`-Markers im ausgelieferten `index.html`. Der Marker ist
 * der Trennungsbeweis, den `scripts/check-gtcc-domain-separation.mjs`
 * misst (`docs/runbooks/GTCC_DOMAIN_SEPARATION_CHECK.md`).
 */
export const ADMIN_SURFACE_META_NAME = "gt-surface";

/** Genau die beiden bekannten Werte, alles andere ergibt `null`. */
export function parseAdminSurface(value: string | undefined | null): AdminSurface | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase();
  return (ADMIN_SURFACES as readonly string[]).includes(trimmed) ? (trimmed as AdminSurface) : null;
}

export interface ResolveAdminSurfaceOptions {
  readonly env?: NodeJS.ProcessEnv | Record<string, string | undefined>;
}

/**
 * Die Oberflaeche dieses Bundles. Liest `GT_ADMIN_SURFACE` aus der
 * uebergebenen Umgebung — im Client `import.meta.env` (Vite reicht den
 * `GT_`-Praefix durch, siehe `apps/admin/vite.config.ts`), im Bau
 * `process.env`.
 */
export function resolveAdminSurface(opts: ResolveAdminSurfaceOptions = {}): AdminSurface {
  const env = opts.env ?? process.env;
  return parseAdminSurface(env[ADMIN_SURFACE_ENV_KEY]) ?? DEFAULT_ADMIN_SURFACE;
}

/** True nur fuer das Cockpit-Artefakt (`/srv/gtcc`). */
export function isCockpitSurface(opts: ResolveAdminSurfaceOptions = {}): boolean {
  return resolveAdminSurface(opts) === "gtcc-cockpit";
}
